import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Shield,
  Home,
  BookOpen,
  Map,
  Clock,
  Users,
  Scroll,
  ExternalLink,
  Search,
  ChevronDown,
  Flag,
  Image,
  Sparkles,
} from 'lucide-react';

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  const navItems = [
    { path: '/', label: 'Home', icon: Home, exact: true },
    { path: '/about', label: 'About', icon: Scroll, exact: true },
    {
      path: '/philosophy',
      label: 'The Quiet Shift',
      icon: Sparkles,
      exact: true,
      quietShift: true,
    },

    {
      label: 'Storyworld',
      icon: Users,
      children: [
        {
          path: '/explore',
          label: 'Storyworld Explorer',
          icon: Scroll,
          exact: true,
        },
        {
          path: '/characters/timeline',
          label: 'Scene Timeline',
          icon: Clock,
        },
        { path: '/books', label: 'Books', icon: BookOpen },
        {
          path: '/characters',
          label: 'Characters',
          icon: Users,
          exact: true,
          children: [
            {
              path: '/characters/tribes',
              label: 'By Tribe',
              icon: Users,
            },
            {
              path: '/characters/roles',
              label: 'By Role',
              icon: Users,
            },
            {
              path: '/characters/allegiance',
              label: 'By Allegiance',
              icon: Users,
            },
          ],
        },
        {
          path: '/media',
          label: 'Images and Video',
          icon: Image,
          exact: true,
        },
        {
          path: '/links',
          label: 'Links',
          icon: ExternalLink,
          exact: true,
        },
      ],
    },

    { path: '/timeline', label: 'Timeline', icon: Clock },

    {
      label: 'Maps',
      icon: Map,
      children: [
        {
          path: '/maps',
          label: 'All Interactive Maps',
          icon: Map,
          exact: true,
        },
        {
          path: '/maps/legions',
          label: 'Legions Timeline',
          icon: Flag,
          exact: true,
        },
      ],
    },

    {
      label: 'Research',
      icon: Search,
      children: [
        {
          path: '/library',
          label: 'Research Home',
          icon: Scroll,
          exact: true,
        },
         {
          path: '/investigations',
          label: 'Featured Investigations',
          icon: Sparkles,
          featured: true,
        },
        {
          path: '/research',
          label: 'Research Papers & Archive Assistant',
          icon: Search,
          exact: true,
        },
        {
          path: '/military',
          label: 'Roman Military Archive',
          icon: Flag,
        },
        {
          path: '/research/conquest',
          label: 'Conquest of Britannia',
          icon: Scroll,
          exact: true,
        },
        {
          path: '/research/roman-terms',
          label: 'Roman Terms & Vocabulary',
          icon: BookOpen,
          exact: true,
        },
      ],
    },
  ];

  const isActivePath = (path, exact = false) => {
    if (!path) return false;
    if (exact) return location.pathname === path;

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };

  const hasChildren = (item) =>
    Array.isArray(item.children) && item.children.length > 0;

  const isItemActive = (item) => {
    const ownPathActive = item.path
      ? isActivePath(item.path, item.exact ?? false)
      : false;

    return (
      ownPathActive ||
      (hasChildren(item) && item.children.some((child) => isItemActive(child)))
    );
  };

  const isParentActive = (item) =>
    hasChildren(item) && item.children.some((child) => isItemActive(child));

  const submenuKey = (parentLabel, childLabel) =>
    `${parentLabel}:${childLabel}`;

  const activeSubmenuFor = (parentLabel) => {
    const parent = navItems.find((item) => item.label === parentLabel);

    const activeNestedChild = parent?.children?.find(
      (child) =>
        hasChildren(child) &&
        child.children.some((nestedChild) => isItemActive(nestedChild))
    );

    return activeNestedChild
      ? submenuKey(parentLabel, activeNestedChild.label)
      : null;
  };

  const toggleDropdown = (label) => {
    if (openDropdown === label) {
      setOpenDropdown(null);
      setOpenSubmenu(null);
      return;
    }

    setOpenDropdown(label);
    setOpenSubmenu(activeSubmenuFor(label));
  };

  const toggleSubmenu = (key) => {
    setOpenSubmenu((current) => (current === key ? null : key));
  };

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setOpenSubmenu(null);
  };

  useEffect(() => {
    closeAllMenus();
  }, [location.pathname]);

  useEffect(() => {
    const onDocumentMouseDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setOpenSubmenu(null);
      }
    };

    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAllMenus();
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const HOVER_BG = 'hover:bg-[#1a1a1a]';
  const ACTIVE_BG = 'bg-[#ff4b00]';
  const ACTIVE_TEXT = 'text-white';

  const dropdownBase =
    'absolute left-0 top-full z-[9999] mt-2 w-80 overflow-hidden rounded-lg border border-white/10 bg-[#101010]/95 shadow-2xl backdrop-blur pointer-events-auto';

  const childClasses = (child, active, mobile = false) => {
    const spacing = mobile
      ? 'flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium'
      : 'flex items-center gap-3 border-b border-white/5 px-4 py-3 text-sm last:border-b-0';

    if (active) {
      return `${spacing} ${ACTIVE_BG} ${ACTIVE_TEXT}`;
    }

    if (child.featured) {
      return `${spacing} bg-amber-500/10 text-amber-200 hover:bg-amber-500/20`;
    }

    return `${spacing} ${HOVER_BG}`;
  };

  const nestedChildClasses = (active, mobile = false) => {
    const spacing = mobile
      ? 'flex items-center gap-3 rounded-md py-2.5 pl-9 pr-3 text-[0.95rem] font-medium'
      : 'flex items-center gap-3 border-t border-white/5 py-2.5 pl-10 pr-4 text-sm';

    return active
      ? `${spacing} ${ACTIVE_BG} ${ACTIVE_TEXT}`
      : `${spacing} bg-black/15 ${HOVER_BG}`;
  };

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-[2000] border-b border-white/10 bg-[#0b0b0b] text-[#f6e4a6] shadow-lg"
      aria-label="Primary navigation"
    >
      <div className="w-full bg-[#0b0b0b]/95 px-4 py-3 backdrop-blur md:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link
            to="/"
            className="flex shrink-0 items-center space-x-3 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-300"
            onClick={closeAllMenus}
            aria-label="Roman Storyworld home"
          >
            <Shield className="h-7 w-7 text-[#f6e4a6] sm:h-6 sm:w-6" />
            <div className="flex flex-col">
              <span className="text-xl font-bold leading-tight sm:text-lg">
                Roman
              </span>
              <span className="text-xl font-bold leading-tight sm:text-lg">
                Storyworld
              </span>
            </div>
          </Link>

          <div className="hidden flex-1 items-center justify-end gap-1 lg:flex xl:gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const itemHasChildren = hasChildren(item);

              if (itemHasChildren) {
                const active = isParentActive(item);
                const dropdownOpen = openDropdown === item.label;
                const menuId = `${item.label
                  .toLowerCase()
                  .replace(/\s+/g, '-')}-desktop-menu`;

                return (
                  <div key={item.label} className="relative">
                    <button
                      type="button"
                      aria-current={active ? 'page' : undefined}
                      aria-haspopup="menu"
                      aria-expanded={dropdownOpen}
                      aria-controls={menuId}
                      onClick={() => toggleDropdown(item.label)}
                      className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition-all duration-200 xl:px-3 ${
                        active
                          ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                          : HOVER_BG
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          active ? 'text-white' : 'text-[#f6e4a6]'
                        }`}
                      />
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          dropdownOpen ? 'rotate-180' : ''
                        } ${
                          active ? 'text-white' : 'text-[#f6e4a6]'
                        }`}
                      />
                    </button>

                    {dropdownOpen && (
                      <div id={menuId} role="menu" className={dropdownBase}>
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childHasChildren = hasChildren(child);
                          const childOwnActive = child.path
                            ? isActivePath(child.path, child.exact ?? false)
                            : false;
                          const childDescendantActive =
                            childHasChildren &&
                            child.children.some((nestedChild) =>
                              isItemActive(nestedChild)
                            );

                          if (childHasChildren) {
                            const key = submenuKey(item.label, child.label);
                            const nestedOpen = openSubmenu === key;
                            const nestedMenuId = `${item.label}-${child.label}`
                              .toLowerCase()
                              .replace(/\s+/g, '-');
                            const branchActive =
                              childOwnActive || childDescendantActive;

                            return (
                              <div
                                key={child.label}
                                className="border-b border-white/5 last:border-b-0"
                              >
                                <div
                                  className={`flex items-stretch transition-colors duration-200 ${
                                    childOwnActive
                                      ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                                      : childDescendantActive
                                        ? 'bg-[#181818] text-white'
                                        : HOVER_BG
                                  }`}
                                >
                                  <Link
                                    to={child.path}
                                    role="menuitem"
                                    onClick={closeAllMenus}
                                    aria-current={
                                      childOwnActive ? 'page' : undefined
                                    }
                                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-sm"
                                  >
                                    <ChildIcon
                                      className={`h-4 w-4 shrink-0 ${
                                        branchActive
                                          ? 'text-white'
                                          : 'text-[#f6e4a6]'
                                      }`}
                                    />
                                    <span className="flex-1">
                                      {child.label}
                                    </span>
                                  </Link>

                                  <button
                                    type="button"
                                    role="menuitem"
                                    aria-haspopup="menu"
                                    aria-expanded={nestedOpen}
                                    aria-controls={`${nestedMenuId}-desktop`}
                                    aria-label={`Show ${child.label} browsing options`}
                                    onClick={() => toggleSubmenu(key)}
                                    className="flex w-12 shrink-0 items-center justify-center border-l border-white/10 transition-colors hover:bg-white/5"
                                  >
                                    <ChevronDown
                                      className={`h-4 w-4 transition-transform duration-200 ${
                                        nestedOpen ? 'rotate-180' : ''
                                      } ${
                                        branchActive
                                          ? 'text-white'
                                          : 'text-[#f6e4a6]'
                                      }`}
                                    />
                                  </button>
                                </div>

                                {nestedOpen && (
                                  <div
                                    id={`${nestedMenuId}-desktop`}
                                    role="menu"
                                    className="bg-black/15"
                                  >
                                    {child.children.map((nestedChild) => {
                                      const NestedIcon = nestedChild.icon;
                                      const nestedActive = isItemActive(
                                        nestedChild
                                      );

                                      return (
                                        <Link
                                          key={nestedChild.path}
                                          to={nestedChild.path}
                                          role="menuitem"
                                          onClick={closeAllMenus}
                                          aria-current={
                                            nestedActive ? 'page' : undefined
                                          }
                                          className={`${nestedChildClasses(
                                            nestedActive
                                          )} transition-all duration-200`}
                                        >
                                          <NestedIcon
                                            className={`h-4 w-4 shrink-0 ${
                                              nestedActive
                                                ? 'text-white'
                                                : 'text-[#f6e4a6]'
                                            }`}
                                          />
                                          <span className="flex-1">
                                            {nestedChild.label}
                                          </span>
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              role="menuitem"
                              onClick={closeAllMenus}
                              aria-current={
                                childOwnActive ? 'page' : undefined
                              }
                              className={`${childClasses(
                                child,
                                childOwnActive
                              )} transition-all duration-200`}
                            >
                              <ChildIcon
                                className={`h-4 w-4 shrink-0 ${
                                  childOwnActive
                                    ? 'text-white'
                                    : child.featured
                                      ? 'text-amber-300'
                                      : 'text-[#f6e4a6]'
                                }`}
                              />
                              <span className="flex-1">{child.label}</span>
                              {child.featured && !childOwnActive && (
                                <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
                                  Featured
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const active = isActivePath(
                item.path,
                item.exact ?? false
              );

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  onClick={closeAllMenus}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-medium transition-all duration-200 xl:px-3 ${
                    active
                      ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                      : item.quietShift
                        ? 'border border-amber-300/25 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20'
                        : HOVER_BG
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 ${
                      active
                        ? 'text-white'
                        : item.quietShift
                          ? 'text-amber-300'
                          : 'text-[#f6e4a6]'
                    }`}
                  />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            className={`ml-4 inline-flex h-12 w-12 items-center justify-center rounded-md transition-colors duration-200 lg:hidden ${HOVER_BG}`}
            aria-label={
              isMobileMenuOpen
                ? 'Close navigation menu'
                : 'Open navigation menu'
            }
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-drawer"
          >
            {isMobileMenuOpen ? (
              <X className="h-7 w-7" />
            ) : (
              <Menu className="h-7 w-7" />
            )}
          </button>
        </div>
      </div>

      <div
        id="mobile-navigation-drawer"
        className={`border-t border-white/10 bg-[#101010]/98 backdrop-blur transition-all duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen
            ? 'max-h-[calc(100svh-76px)] overflow-y-auto opacity-100'
            : 'max-h-0 overflow-hidden opacity-0'
        }`}
      >
        <div className="space-y-1 px-4 py-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const itemHasChildren = hasChildren(item);

            if (itemHasChildren) {
              const active = isParentActive(item);
              const expanded = openDropdown === item.label;
              const menuId = `${item.label
                .toLowerCase()
                .replace(/\s+/g, '-')}-mobile-menu`;

              return (
                <div key={item.label} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleDropdown(item.label)}
                    aria-expanded={expanded}
                    aria-controls={menuId}
                    className={`flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium transition-all duration-200 ${
                      active
                        ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                        : HOVER_BG
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon
                        className={`h-5 w-5 ${
                          active ? 'text-white' : 'text-[#f6e4a6]'
                        }`}
                      />
                      <span>{item.label}</span>
                    </span>

                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${
                        expanded ? 'rotate-180' : ''
                      } ${
                        active ? 'text-white' : 'text-[#f6e4a6]'
                      }`}
                    />
                  </button>

                  {expanded && (
                    <div
                      id={menuId}
                      className="ml-5 space-y-1 border-l border-white/10 pl-3"
                    >
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childHasChildren = hasChildren(child);
                        const childOwnActive = child.path
                          ? isActivePath(child.path, child.exact ?? false)
                          : false;
                        const childDescendantActive =
                          childHasChildren &&
                          child.children.some((nestedChild) =>
                            isItemActive(nestedChild)
                          );

                        if (childHasChildren) {
                          const key = submenuKey(item.label, child.label);
                          const nestedOpen = openSubmenu === key;
                          const nestedMenuId = `${item.label}-${child.label}`
                            .toLowerCase()
                            .replace(/\s+/g, '-');
                          const branchActive =
                            childOwnActive || childDescendantActive;

                          return (
                            <div key={child.label} className="space-y-1">
                              <div
                                className={`flex items-stretch overflow-hidden rounded-md transition-colors duration-200 ${
                                  childOwnActive
                                    ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                                    : childDescendantActive
                                      ? 'bg-[#181818] text-white'
                                      : HOVER_BG
                                }`}
                              >
                                <Link
                                  to={child.path}
                                  onClick={closeAllMenus}
                                  aria-current={
                                    childOwnActive ? 'page' : undefined
                                  }
                                  className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-base font-medium"
                                >
                                  <ChildIcon
                                    className={`h-5 w-5 shrink-0 ${
                                      branchActive
                                        ? 'text-white'
                                        : 'text-[#f6e4a6]'
                                    }`}
                                  />
                                  <span className="flex-1">
                                    {child.label}
                                  </span>
                                </Link>

                                <button
                                  type="button"
                                  aria-expanded={nestedOpen}
                                  aria-controls={`${nestedMenuId}-mobile`}
                                  aria-label={`Show ${child.label} browsing options`}
                                  onClick={() => toggleSubmenu(key)}
                                  className="flex w-12 shrink-0 items-center justify-center border-l border-white/10 transition-colors hover:bg-white/5"
                                >
                                  <ChevronDown
                                    className={`h-5 w-5 transition-transform duration-200 ${
                                      nestedOpen ? 'rotate-180' : ''
                                    } ${
                                      branchActive
                                        ? 'text-white'
                                        : 'text-[#f6e4a6]'
                                    }`}
                                  />
                                </button>
                              </div>

                              {nestedOpen && (
                                <div
                                  id={`${nestedMenuId}-mobile`}
                                  className="ml-3 space-y-1 border-l border-white/10 pl-2"
                                >
                                  {child.children.map((nestedChild) => {
                                    const NestedIcon = nestedChild.icon;
                                    const nestedActive = isItemActive(
                                      nestedChild
                                    );

                                    return (
                                      <Link
                                        key={nestedChild.path}
                                        to={nestedChild.path}
                                        onClick={closeAllMenus}
                                        aria-current={
                                          nestedActive ? 'page' : undefined
                                        }
                                        className={`${nestedChildClasses(
                                          nestedActive,
                                          true
                                        )} transition-all duration-200`}
                                      >
                                        <NestedIcon
                                          className={`h-5 w-5 shrink-0 ${
                                            nestedActive
                                              ? 'text-white'
                                              : 'text-[#f6e4a6]'
                                          }`}
                                        />
                                        <span className="flex-1">
                                          {nestedChild.label}
                                        </span>
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={closeAllMenus}
                            aria-current={
                              childOwnActive ? 'page' : undefined
                            }
                            className={`${childClasses(
                              child,
                              childOwnActive,
                              true
                            )} transition-all duration-200`}
                          >
                            <ChildIcon
                              className={`h-5 w-5 shrink-0 ${
                                childOwnActive
                                  ? 'text-white'
                                  : child.featured
                                    ? 'text-amber-300'
                                    : 'text-[#f6e4a6]'
                              }`}
                            />
                            <span className="flex-1">{child.label}</span>
                            {child.featured && !childOwnActive && (
                              <span className="rounded-full border border-amber-400/30 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-200">
                                Featured
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActivePath(
              item.path,
              item.exact ?? false
            );

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeAllMenus}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium transition-all duration-200 ${
                  active
                    ? `${ACTIVE_BG} ${ACTIVE_TEXT}`
                    : item.quietShift
                      ? 'border border-amber-300/25 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20'
                      : HOVER_BG
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${
                    active
                      ? 'text-white'
                      : item.quietShift
                        ? 'text-amber-300'
                        : 'text-[#f6e4a6]'
                  }`}
                />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
